/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { AdresseForm } from '@mairistem/adresses';

import { Checkbox, Divider, Form, Grid } from '@mairistem/composants';

import { type Adresse, type Individu, ListeRecensement } from '@/types';

import { CoordonneesContacts } from '@mairistem/administres-utils';

import validationAdresse from '@/utils/validations/validationAdresse';
import validationInformationsContact from '@/utils/validations/validationInformationsContact';
import { formatAdresse } from '@/utils';

import PanelModalEdition from '../PanelModalEdition';

interface InformationContactModalProps {
  tel1: string;
  tel2: string;
  mail: string;
  adresseContact: Partial<Adresse>;
  adresseRattachement: Partial<Adresse>;
  updated: boolean;
  onChange: (data: Partial<Individu>) => void;
  onSave: () => Promise<void>;
  onClose: () => void;
  onCancel: () => void;
}

const InformationContactModal = (props: InformationContactModalProps) => {
  const {
    tel1,
    tel2,
    mail,
    adresseContact,
    adresseRattachement,
    updated,
    onChange,
    onSave,
    onClose,
    onCancel,
  } = props;

  const [loading, setLoading] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<
    Partial<Individu> & { api?: boolean }
  >({});

  const [localCoordonnees, setLocalCoordonnees] = React.useState({
    tel1,
    tel2,
    mail,
  });

  const handleItemChange = React.useCallback(
    ({ adresse }) => {
      onChange({
        adresseContact: formatAdresse(adresseContact, adresse),
      });
    },
    [onChange, adresseContact],
  );

  const handleAdresseDomicileEgalResidence = React.useCallback(() => {
    onChange({
      adresseContact: adresseRattachement,
    });
  }, [adresseRattachement, onChange]);

  const handleAdresseChange = ({ target: { name, value } }) => {
    if (name === 'adresse.type') {
      onChange({
        adresseContact: {
          type: value,
          identifiantVoie: null,
          identifiantCommune: null,
          identifiantCodePostal: null,
        },
      });
    }
  };

  const handleCoordonneesChange = React.useCallback(
    (
      _e: React.ChangeEvent<HTMLInputElement>,
      data: { name: string; value: string },
    ) => {
      setLocalCoordonnees((prev) => ({
        ...prev,
        [data.name]: data.value,
      }));
    },
    [],
  );

  const handleSave = React.useCallback(() => {
    const adresseErrors = validationAdresse(adresseContact);
    const contactErrors = validationInformationsContact(
      localCoordonnees,
      false,
      false,
    );

    const e = {
      ...adresseErrors,
      ...contactErrors,
    };

    setErrors(e);

    if (_.isEmpty(e)) {
      setLoading(true);

      onChange({
        ...localCoordonnees,
        tel1: localCoordonnees.tel1 || '',
        tel2: localCoordonnees.tel2 || '',
      });
    }
  }, [adresseContact, localCoordonnees, onChange]);

  React.useEffect(() => {
    if (
      loading &&
      (tel1 || '') === (localCoordonnees.tel1 || '') &&
      (tel2 || '') === (localCoordonnees.tel2 || '') &&
      mail === localCoordonnees.mail
    ) {
      onSave()
        .then(() => {
          onClose();
        })
        .catch(() => {
          setErrors({ api: true });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [loading, tel1, tel2, mail, localCoordonnees, onSave, onClose]);

  const isUpdated = React.useMemo(
    () => updated || !_.isEqual(localCoordonnees, { tel1, tel2, mail }),
    [updated, localCoordonnees, tel1, tel2, mail],
  );

  return (
    <PanelModalEdition
      errors={errors}
      loading={loading}
      header="Informations de contact"
      updated={isUpdated}
      onSave={handleSave}
      onClose={onCancel}
      size="large"
    >
      <Grid as={Form}>
        <Grid.Row>
          <Grid.Column width={16}>
            <AdresseForm
              title={
                <Checkbox
                  toggle
                  name="adresseDomicileEgalResidence"
                  label="Même adresse que l'adresse de rattachement"
                  data-testid="adresse-domicile-form-egal-residence"
                  checked={_.isEqual(
                    _.omit(adresseContact, 'identifiant'),
                    _.omit(adresseRattachement, 'identifiant'),
                  )}
                  onChange={handleAdresseDomicileEgalResidence}
                />
              }
              saisie="libre"
              displayDepartement={false}
              handleTypes
              adresse={{
                ...adresseContact,
                codePostal: adresseContact?.libelleCodePostal,
                commune: adresseContact?.libelleCommune,
                voie: adresseContact?.libelleVoie,
                pays: adresseContact?.libellePays,
              }}
              onItemChange={handleItemChange}
              onChange={handleAdresseChange}
            />
          </Grid.Column>
        </Grid.Row>
        <Divider horizontal className="required">
          COORDONNEES DE CONTACT
        </Divider>
        <Grid.Row>
          <Grid.Column width={16}>
            <CoordonneesContacts
              tel1={localCoordonnees.tel1}
              tel2={localCoordonnees.tel2}
              mail={localCoordonnees.mail}
              // Dans la fiche on veut pas que le mail soit obligatoire, ni le tel donc on simule recensement a non recensés tout le temps
              recensement={ListeRecensement.NonRecenses}
              inline
              onChange={handleCoordonneesChange}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </PanelModalEdition>
  );
};

export default InformationContactModal;
